<template>
  <div class="w-screen bg-primary-0 mx-auto max-h-screen h-screen overflow-y-scroll " :class="{
    'flex flex-col justify-center': loading,
    'max-w-[620px]': isWeb
  }" id="appWrapper" :style="{
    'padding-top': topSafeArea + 'px',
    'padding-bottom': bottomSafeArea + 'px'
  }">
    <router-view v-if="!loading" v-slot="{ Component }">
      <transition name="scale" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>

    <template v-else>
      <div class="text-center text-3xl font-bold text-white mt-5 mb-3">
        {{ $t('loading_app') }}
      </div>
      <Loader class="w-[230px] h-auto mx-auto" />
    </template>
  </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from "vue";
import useContentStore from "@/stores/contentStore";
import useDeliveryStore, { DELIVERY_TYPES } from "@/stores/deliveryStore";
import usePluStore from "@/stores/pluStore";
import useCartStore from "@/stores/cartStore";
import Loader from "@/components/Loader.vue";
import { topSafeArea, bottomSafeArea } from "@/plugins/capacitor/SafeArea";
import { useI18n } from "vue-i18n";
import { platform } from "@/plugins/capacitor/SafeArea";
import { App } from "@capacitor/app";

const deliveryStore = useDeliveryStore()
const contentStore = useContentStore()
const pluStore = usePluStore()
const cartStore = useCartStore()

contentStore.loadAppContent()
deliveryStore.loadDeliveryPlaces()
pluStore.loadAppSettings()

// Pending deep link - ukladame ak pride pred nacitanim aplikacie
const pendingDeepLink = ref(null)
const processingDeepLink = ref(false)

// Deep link handler pre QR kody stolov
const handleDeepLink = (url) => {
  console.log('Deep link received:', url)

  // Format: hajcer://table/{place}/{blueGastroId}/{tableNumber}
  if (url && url.startsWith('hajcer://table/')) {
    const parts = url.replace('hajcer://table/', '').split('/')
    if (parts.length >= 3) {
      const place = parts[0]           // BO alebo PE
      const pluTableId = parseInt(parts[1])  // BlueGastro ID stola
      const tableNumber = parts[2]      // Cislo stola

      // Validacia miesta
      if (place === 'BO' || place === 'PE') {
        console.log('Setting table order:', { place, pluTableId, tableNumber })

        // Ulozime pending deep link - data sa aplikuju az po nacitani
        pendingDeepLink.value = { place, pluTableId, tableNumber }

        // Ak aplikacia uz je nacitana, spracujeme hned
        if (!loading.value) {
          void processPendingDeepLink()
        }
      }
    }
  }
}

// Spracovanie pending deep linku po nacitani aplikacie
const processPendingDeepLink = async () => {
  if (!pendingDeepLink.value || processingDeepLink.value) return

  const deepLink = pendingDeepLink.value
  pendingDeepLink.value = null
  processingDeepLink.value = true
  let failed = false

  try {
    const { place, pluTableId, tableNumber } = deepLink
    console.log('Processing pending deep link:', deepLink)

    // Nastavenie delivery typu a stola
    deliveryStore.setDeliveryType(DELIVERY_TYPES.TABLE_ORDER)
    deliveryStore.setTableOrder(place, tableNumber, pluTableId)

    // Vycistenie kosika
    cartStore.itemsInCart = []

    // Premapovanie obsahu pre dane miesto (teraz uz mame data)
    await contentStore.mapAppContent()

  } catch (error) {
    failed = true
    console.error('Deep link could not be processed:', error)
    if (!pendingDeepLink.value) pendingDeepLink.value = deepLink
  } finally {
    const nextDeepLink = pendingDeepLink.value
    processingDeepLink.value = false
    if (nextDeepLink && (!failed || nextDeepLink !== deepLink)) {
      void processPendingDeepLink()
    }
  }
}

// Listener pre deep links
App.addListener('appUrlOpen', (event) => {
  handleDeepLink(event.url)
})

// Check for deep link on app launch
onMounted(async () => {
  try {
    const urlOpen = await App.getLaunchUrl()
    if (urlOpen && urlOpen.url) {
      handleDeepLink(urlOpen.url)
    }
  } catch (e) {
    console.log('No launch URL')
  }
})

const loading = ref(true)
const appIsLoading = computed(() => contentStore.appContentLoading || deliveryStore.loading || pluStore.loading)

watch(() => appIsLoading.value, (value) => {
  if (!value) {
    setTimeout(() => {
      loading.value = false
      // Spracovanie pending deep linku po nacitani
      void processPendingDeepLink()
    }, 1000)
  } else {
    loading.value = true
  }
})

const isWeb = platform === 'web'


</script>
